import {
  azureStorageService,
  isMissingBlobNotFoundError,
} from "./azureStorageService";
import { timetableDB } from "../db/indexedDb";
import { screenDB } from "../db/screenDb";
import { isHomeCode, isMasjidScreenCode } from "../utils/homeScreen";
import { PrayerTimes, HijriDateComponents } from "../types/PrayerTypes";
import { OfflineRoutingDiagnostics, ScreenData } from "../types/ScreenTypes";
import axios from "axios";
import { runtimeConfig } from "../config/runtime";

// Add online status detection
let isOnline = navigator.onLine;
window.addEventListener("online", () => {
  isOnline = true;
});
window.addEventListener("offline", () => {
  isOnline = false;
});

// Add deduplication cache for in-flight requests
const pendingRequests = new Map<string, Promise<any>>();
const DEDUPLICATION_TTL = 5000; // 5 seconds
const DEFAULT_ROUTING_SNAPSHOT_TTL_SECONDS = 300;
const MISSING_TIMETABLE_FALLBACK_MARKER = "__missing_timetable__";

type RoutingRule = {
  ruleId?: string;
  name?: string;
  enabled?: boolean;
  priority?: number;
  conditionType?: string;
  condition?: any;
  actionType?: string;
  action?: {
    targetScreenCode?: string;
    intensityPercent?: number;
  };
};

function withOfflineDiagnostics(
  screen: ScreenData,
  diagnostics: Omit<OfflineRoutingDiagnostics, "source" | "evaluatedAtUtc">,
): ScreenData {
  return {
    ...screen,
    offlineRoutingDiagnostics: {
      source: "offline-cache",
      evaluatedAtUtc: new Date().toISOString(),
      ...diagnostics,
    },
  };
}

function hasRoutingSnapshot(screenData: ScreenData): boolean {
  return !!(
    screenData.routingRulesJson && screenData.routingSnapshotFetchedAtUtc
  );
}

function isRoutingSnapshotFresh(screenData: ScreenData): boolean {
  if (!hasRoutingSnapshot(screenData)) {
    return true;
  }

  const fetchedAtMs = Date.parse(
    screenData.routingSnapshotFetchedAtUtc as string,
  );
  if (Number.isNaN(fetchedAtMs)) {
    return false;
  }

  const ttlSeconds =
    screenData.routingRulesTtlSeconds ?? DEFAULT_ROUTING_SNAPSHOT_TTL_SECONDS;
  const ttlMs = Math.max(30, ttlSeconds) * 1000;
  return Date.now() - fetchedAtMs <= ttlMs;
}

function parseRoutingRules(screenData: ScreenData): RoutingRule[] {
  if (!screenData.routingRulesJson) {
    return [];
  }

  try {
    const parsed = JSON.parse(screenData.routingRulesJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getTimeParts(timeZone?: string) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    weekday: "short",
  });

  const parts = formatter.formatToParts(new Date());
  const getPart = (type: string) =>
    parts.find((x) => x.type === type)?.value || "";
  const weekday = getPart("weekday");
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  return {
    year: parseInt(getPart("year"), 10),
    month: parseInt(getPart("month"), 10),
    day: parseInt(getPart("day"), 10),
    hour: parseInt(getPart("hour"), 10),
    minute: parseInt(getPart("minute"), 10),
    second: parseInt(getPart("second"), 10),
    dayOfWeek: weekdayMap[weekday] ?? new Date().getDay(),
  };
}

function parseTimeToSeconds(value?: string | null): number | null {
  if (!value || typeof value !== "string") {
    return null;
  }

  const parts = value.split(":");
  if (parts.length < 2) {
    return null;
  }

  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parts.length > 2 ? parseInt(parts[2], 10) : 0;
  if ([h, m, s].some((x) => Number.isNaN(x))) {
    return null;
  }

  return h * 3600 + m * 60 + s;
}

export function extractTimetableDays(timetable: any): any[] {
  if (Array.isArray(timetable)) return timetable;
  if (Array.isArray(timetable?.data)) return timetable.data;
  if (Array.isArray(timetable?.days)) return timetable.days;
  if (Array.isArray(timetable?.data?.days)) return timetable.data.days;
  return [];
}

function getFieldValue(source: any, key: string): string | null {
  if (!source) return null;
  const direct = source[key];
  if (typeof direct === "string") return direct;
  const lower = source[key.toLowerCase()];
  if (typeof lower === "string") return lower;
  return null;
}

function getPrayerMinutesByField(
  dayRow: any,
  prayerField?: string | null,
): number | null {
  if (!dayRow || !prayerField) {
    return null;
  }

  const raw = getFieldValue(dayRow, prayerField);
  if (!raw || raw === "--:--") {
    return null;
  }

  const parts = raw.split(":");
  if (parts.length < 2) {
    return null;
  }

  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }

  return h * 60 + m;
}

async function getTimetableDayForNow(
  masjidId: string | number,
  timeZone?: string,
): Promise<any | null> {
  const now = getTimeParts(timeZone);
  const timetable = await timetableDB.getTimetable(
    now.year.toString(),
    now.month.toString(),
    masjidId.toString(),
  );
  const days = extractTimetableDays(timetable);
  if (days.length === 0) {
    return null;
  }

  const dayStr = now.day.toString();
  const dayPadded = now.day.toString().padStart(2, "0");
  return (
    days.find((d: any) => {
      const dateVal = (d?.Date ?? d?.date ?? "").toString();
      return dateVal === dayStr || dateVal === dayPadded;
    }) || null
  );
}

function isRuleDayMatch(
  condition: any,
  now: ReturnType<typeof getTimeParts>,
): boolean {
  const days = Array.isArray(condition?.daysOfWeek) ? condition.daysOfWeek : [];
  if (days.length === 0) {
    return true;
  }
  return days.includes(now.dayOfWeek);
}

function evaluateRule(
  rule: RoutingRule,
  now: ReturnType<typeof getTimeParts>,
  timetableDay: any,
): boolean {
  const condition = rule.condition || {};
  if (!isRuleDayMatch(condition, now)) {
    return false;
  }

  const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
  const conditionType = (rule.conditionType || "Always").toLowerCase();

  if (conditionType === "always") {
    return true;
  }

  if (
    conditionType === "timewindow" ||
    conditionType === "crossdaytimewindow"
  ) {
    const start = parseTimeToSeconds(condition.startTime);
    const end = parseTimeToSeconds(condition.endTime);
    if (start == null || end == null) {
      return false;
    }

    if (conditionType === "timewindow") {
      return currentSeconds >= start && currentSeconds < end;
    }

    return start <= end
      ? currentSeconds >= start && currentSeconds < end
      : currentSeconds >= start || currentSeconds < end;
  }

  if (conditionType === "gregoriandate" || conditionType === "hijridate") {
    const months = Array.isArray(condition.months) ? condition.months : [];
    const days = Array.isArray(condition.days) ? condition.days : [];
    const monthMatch = months.length === 0 || months.includes(now.month);
    const dayMatch = days.length === 0 || days.includes(now.day);
    if (!monthMatch || !dayMatch) {
      return false;
    }

    const start = parseTimeToSeconds(condition.startTime);
    const end = parseTimeToSeconds(condition.endTime);
    if (start == null || end == null) {
      return true;
    }

    return start <= end
      ? currentSeconds >= start && currentSeconds < end
      : currentSeconds >= start || currentSeconds < end;
  }

  if (
    conditionType === "prayerwindow" ||
    conditionType === "crossdayprayerwindow" ||
    conditionType === "prayerbased"
  ) {
    if (!timetableDay) {
      return false;
    }

    if (conditionType === "prayerbased") {
      const rawScheduleType = (
        condition.prayerScheduleType || ""
      ).toLowerCase();
      const scheduleType =
        rawScheduleType === "after-start" || rawScheduleType === "after-end"
          ? "after"
          : rawScheduleType;
      const afterPhase =
        rawScheduleType === "after-start" || rawScheduleType === "after-end"
          ? rawScheduleType
          : (condition.afterPhase || "after-end").toLowerCase();

      if (scheduleType === "before") {
        if (!condition.startPrayer) return false;
        const prayerStart = getPrayerMinutesByField(
          timetableDay,
          condition.startPrayer,
        );
        if (prayerStart == null) return false;

        const prayerStartSeconds = prayerStart * 60;
        const beforeOffsetSeconds = condition.secondsBeforeStartPrayer ?? 0;
        const windowStartSeconds = prayerStartSeconds - beforeOffsetSeconds;
        return (
          currentSeconds >= windowStartSeconds &&
          currentSeconds < prayerStartSeconds
        );
      }

      if (scheduleType === "after") {
        if (!condition.startPrayer) return false;
        const prayerStart = getPrayerMinutesByField(
          timetableDay,
          condition.startPrayer,
        );
        if (prayerStart == null) return false;

        const prayerStartSeconds = prayerStart * 60;
        if (afterPhase === "after-start") {
          const afterStartSeconds = condition.secondsAfterStartPrayer ?? 0;
          const windowEndSeconds = prayerStartSeconds + afterStartSeconds;
          return (
            currentSeconds >= prayerStartSeconds &&
            currentSeconds < windowEndSeconds
          );
        }

        const endPrayerName = condition.endPrayer || condition.startPrayer;
        const prayerEnd = getPrayerMinutesByField(timetableDay, endPrayerName);
        if (prayerEnd == null) return false;

        const prayerEndSeconds = prayerEnd * 60;
        const afterEndSeconds = condition.secondsAfterEndPrayer ?? 0;
        const windowEndSeconds = prayerEndSeconds + afterEndSeconds;
        return (
          currentSeconds >= prayerEndSeconds &&
          currentSeconds < windowEndSeconds
        );
      }

      const startPrayer = condition.startPrayer || "JamMaghrib";
      const endPrayer = condition.endPrayer || "Sunrise";
      const startMinutes = getPrayerMinutesByField(timetableDay, startPrayer);
      const endMinutes = getPrayerMinutesByField(timetableDay, endPrayer);
      if (startMinutes == null || endMinutes == null) return false;

      const effectiveStartSeconds =
        startMinutes * 60 - (condition.secondsBeforeStartPrayer ?? 0);
      const effectiveEndSeconds =
        endMinutes * 60 + (condition.secondsAfterEndPrayer ?? 0);
      return effectiveStartSeconds < effectiveEndSeconds
        ? currentSeconds >= effectiveStartSeconds &&
            currentSeconds < effectiveEndSeconds
        : currentSeconds >= effectiveStartSeconds ||
            currentSeconds < effectiveEndSeconds;
    }

    const startPrayer = condition.startPrayer || "JamMaghrib";
    const endPrayer = condition.endPrayer || "Sunrise";
    const startMinutes = getPrayerMinutesByField(timetableDay, startPrayer);
    const endMinutes = getPrayerMinutesByField(timetableDay, endPrayer);
    if (
      startMinutes == null ||
      endMinutes == null ||
      startMinutes === endMinutes
    ) {
      return false;
    }

    const currentMinutes = now.hour * 60 + now.minute;
    if (conditionType === "prayerwindow") {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }

    return startMinutes < endMinutes
      ? currentMinutes >= startMinutes && currentMinutes < endMinutes
      : currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }

  return false;
}

async function resolveScreenFromOfflineRouting(
  baseScreen: ScreenData,
  masjidId: string | number,
): Promise<ScreenData> {
  if (!baseScreen.routingEnabled) {
    return withOfflineDiagnostics(baseScreen, {
      reason: "routing-disabled",
    });
  }

  const rules = parseRoutingRules(baseScreen)
    .filter((r) => r && r.enabled !== false)
    .sort((a, b) => {
      const priorityCompare = (a.priority ?? 100) - (b.priority ?? 100);
      if (priorityCompare !== 0) {
        return priorityCompare;
      }

      const aRuleId = a.ruleId || "";
      const bRuleId = b.ruleId || "";
      return aRuleId.localeCompare(bRuleId);
    });

  if (rules.length === 0) {
    return withOfflineDiagnostics(baseScreen, {
      reason: "no-rules-configured",
    });
  }

  const now = getTimeParts(baseScreen.timeZone);
  const timetableDay = await getTimetableDayForNow(
    masjidId,
    baseScreen.timeZone,
  );

  for (const rule of rules) {
    if (!evaluateRule(rule, now, timetableDay)) {
      continue;
    }

    const actionType = (rule.actionType || "").toLowerCase();
    if (actionType !== "switch-screen") {
      return withOfflineDiagnostics(baseScreen, {
        reason: "matched-non-switch-action",
        matchedRuleId: rule.ruleId,
        matchedRuleName: rule.name,
        matchedConditionType: rule.conditionType,
        matchedActionType: rule.actionType,
      });
    }

    const targetCode = rule.action?.targetScreenCode?.trim();
    if (!targetCode || targetCode === baseScreen.code) {
      return withOfflineDiagnostics(baseScreen, {
        reason: !targetCode
          ? "switch-screen-target-missing-fallback"
          : "switch-screen-self-fallback",
        matchedRuleId: rule.ruleId,
        matchedRuleName: rule.name,
        matchedConditionType: rule.conditionType,
        matchedActionType: rule.actionType,
        targetScreenCode: targetCode,
      });
    }

    const cachedTargetScreen = await screenDB.getScreenData(targetCode);
    if (!cachedTargetScreen) {
      return withOfflineDiagnostics(baseScreen, {
        reason: "switch-screen-target-not-cached-fallback",
        matchedRuleId: rule.ruleId,
        matchedRuleName: rule.name,
        matchedConditionType: rule.conditionType,
        matchedActionType: rule.actionType,
        targetScreenCode: targetCode,
      });
    }

    return withOfflineDiagnostics(cachedTargetScreen, {
      reason: "matched-switch-screen-offline-cache",
      matchedRuleId: rule.ruleId,
      matchedRuleName: rule.name,
      matchedConditionType: rule.conditionType,
      matchedActionType: rule.actionType,
      targetScreenCode: targetCode,
    });
  }

  return withOfflineDiagnostics(baseScreen, {
    reason: "no-rule-matched",
  });
}

// Azure Best Practice: Offline-first approach with fetch strategy
export async function fetchScreenData(
  masjidId: string | number,
  screenCode: string,
): Promise<ScreenData> {
  // Generate cache key for this request
  const cacheKey = `screenData:${masjidId}:${screenCode}`;
  // Every Home tablet asks for "home", so its offline copy is kept per masjid.
  const storedKey = isHomeCode(screenCode) ? `${masjidId}:${screenCode}` : screenCode;

  // Check if there's already a pending request for this screen
  if (pendingRequests.has(cacheKey)) {
    console.log(
      `Reusing in-flight request for screen data ${screenCode} (masjid ${masjidId})`,
    );
    return pendingRequests.get(cacheKey) as Promise<ScreenData>;
  }

  // First check if we're online
  if (!isOnline) {
    console.log("Device is offline, retrieving cached screen data");
    const cachedData = await screenDB.getScreenData(storedKey);
    if (cachedData) {
      if (!isRoutingSnapshotFresh(cachedData)) {
        // Stale posters beat a dead screen — never error when a snapshot exists
        console.warn("Routing snapshot is stale; using it anyway (offline)");
      }
      const resolvedCachedScreen = await resolveScreenFromOfflineRouting(
        cachedData,
        masjidId,
      );
      console.log(
        "Offline routing diagnostics:",
        resolvedCachedScreen.offlineRoutingDiagnostics,
      );
      console.log("Using cached screen data:", resolvedCachedScreen);
      return resolvedCachedScreen;
    }
    throw new Error("No cached screen data available and device is offline");
  }

  // Create a new screen data request
  const screenDataPromise = (async () => {
    try {
      // No-op once env.js has loaded; retries it if the boot-time load failed
      await runtimeConfig.initialize();

      // Home layouts come in 16:10 (tablets) and 16:9 (TVs, monitors); the screen's shape picks one.
      // Taken from the code's orientation, not the window's, so the offline copy of the other
      // orientation gets the right shape too.
      const long = Math.max(window.innerWidth, window.innerHeight);
      const short = Math.min(window.innerWidth, window.innerHeight);
      // A masjid's own screen has one shape; the window's says which way up a fallback should be.
      const aspect = isMasjidScreenCode(screenCode)
        ? window.innerWidth / window.innerHeight
        : /portrait/i.test(screenCode)
          ? short / long
          : long / short;
      const shape = isHomeCode(screenCode) ? `?aspect=${aspect.toFixed(2)}` : "";

      // Fetch screen data from public API (no authentication required)
      const response = await axios.get(
        `${runtimeConfig.digitalSignageApiDomain}/api/DigitalSignage/screens/${masjidId}/${screenCode}${shape}`,
        { timeout: 10000 },
      );

      // Azure Best Practice: Cache successful responses
      const screenData = response.data;
      await screenDB.saveScreenData(storedKey, screenData);
      return screenData;
    } catch (error) {
      console.error("Error fetching screen data:", error);

      // Azure Best Practice: Graceful degradation - try cached data as fallback
      console.log("Trying to retrieve cached screen data");
      const cachedData = await screenDB.getScreenData(storedKey);
      if (cachedData) {
        if (!isRoutingSnapshotFresh(cachedData)) {
          // Stale posters beat a dead screen — never error when a snapshot exists
          console.warn(
            "Routing snapshot is stale; using it anyway after fetch failure",
          );
        }
        const resolvedCachedScreen = await resolveScreenFromOfflineRouting(
          cachedData,
          masjidId,
        );
        console.log(
          "Offline routing diagnostics:",
          resolvedCachedScreen.offlineRoutingDiagnostics,
        );
        console.log("Using cached screen data after fetch failure");
        return resolvedCachedScreen;
      }
      throw new Error(
        `No cached screen data; fetch from ${runtimeConfig.digitalSignageApiDomain} failed: ${(error as Error)?.message}`,
      );
    } finally {
      // Remove from pending requests after a delay
      setTimeout(() => {
        pendingRequests.delete(cacheKey);
      }, DEDUPLICATION_TTL);
    }
  })();

  // Store the promise for potential reuse
  pendingRequests.set(cacheKey, screenDataPromise);
  return screenDataPromise;
}

// Helper to build Azure blob path for timetable
function getTimetableBlobPath(
  year: string,
  month: string,
  masjidId: string | number,
): string {
  return `timetables/${masjidId}/${year}/${parseInt(month, 10)}.json.gz`;
}

function createMissingTimetableFallback(date: string): PrayerTimes {
  const [year = "--", month = "--", day = "--"] = date.split("-");
  return {
    Date: day,
    Month: month,
    Year: year,
    SehriEnd: "--:--",
    BegFajr: "--:--",
    JamFajr: "--:--",
    Sunrise: "--:--",
    Noon: "--:--",
    BegDhuhr: "--:--",
    JamDhuhr: "--:--",
    BegAsar: "--:--",
    BegAsar2: "--:--",
    JamAsar: "--:--",
    Sunset: "--:--",
    BegMaghrib: "--:--",
    JamMaghrib: "--:--",
    BegEsha: "--:--",
    JamEsha: "--:--",
    Jummah1: "--:--",
    Jummah2: "--:--",
    Jummah3: "--:--",
    Jummah4: "--:--",
    HijriDate: "--",
    GregorianDate: date,
    nextChange: MISSING_TIMETABLE_FALLBACK_MARKER,
  };
}

export function isMissingTimetableFallback(prayerTimes?: PrayerTimes | null) {
  return prayerTimes?.nextChange === MISSING_TIMETABLE_FALLBACK_MARKER;
}

// Fetch timetable data for a month (cache in IndexedDB)
export async function fetchTimetableData(
  year: string,
  month: string,
  masjidId: string | number,
): Promise<any> {
  const normalizedMonth = String(Number(month));

  // Check IndexedDB cache first (fast, ~1ms)
  const cached = await timetableDB.getTimetable(
    year,
    normalizedMonth,
    masjidId.toString(),
  );

  if (cached) {
    console.log(`📚 Using cached timetable for ${year}-${normalizedMonth}`);
    return cached;
  }

  // Offline guard: if no cache and offline, throw descriptive error
  if (!isOnline) {
    throw new Error(
      `No cached timetable for ${year}-${normalizedMonth} and device is offline`,
    );
  }

  // Not cached, fetch from Azure
  console.log(
    `🌐 Fetching timetable from Azure for ${year}-${normalizedMonth}`,
  );
  const blobPath = getTimetableBlobPath(year, normalizedMonth, masjidId);
  const { data } = await azureStorageService.getPrayerTimesFromBlob(
    masjidId,
    blobPath,
  );

  // Save to IndexedDB cache
  await timetableDB.saveTimetable(
    year,
    normalizedMonth,
    masjidId.toString(),
    data,
  );

  return data;
}

// Fetch prayer times for a specific date (from timetable)
export async function fetchPrayerTimes(
  date: string,
  masjidId: string | number,
): Promise<PrayerTimes> {
  const [year, monthWithZero, dayWithZero] = date.split("-");
  const day = String(parseInt(dayWithZero, 10));
  const month = String(parseInt(monthWithZero, 10));

  let timetable;
  try {
    timetable = await fetchTimetableData(year, month, masjidId);
  } catch (error: any) {
    // Never throw: a prayer-times failure (404, timeout, TLS, CORS, offline)
    // must not take down the screen — render empty times and let the sync
    // loop fill them in when the network recovers
    console.warn(
      "[PRAYER-FALLBACK] Timetable unavailable; using empty prayer day",
      {
        masjidId,
        date,
        month: `${year}-${month}`,
        missingBlob: isMissingBlobNotFoundError(error),
        reason: error?.message || String(error),
      },
    );
    return createMissingTimetableFallback(date);
  }

  // Azure best practice: Support both direct and wrapped data structures
  const daysArray = Array.isArray(timetable)
    ? timetable
    : Array.isArray(timetable.data)
      ? timetable.data
      : Array.isArray(timetable.days)
        ? timetable.days
        : Array.isArray(timetable.data?.days)
          ? timetable.data.days
          : undefined;

  if (!Array.isArray(daysArray)) {
    console.error("Timetable data missing or Days is not an array", timetable);
    return createMissingTimetableFallback(date);
  }

  const daily = daysArray.find((d: any) => d.Date === day || d.date === day);
  if (!daily) {
    console.warn(
      "[PRAYER-FALLBACK] Timetable day missing; using empty prayer day",
      {
        masjidId,
        date,
        availableDays: daysArray.length,
      },
    );
    return createMissingTimetableFallback(date);
  }

  return transformToPrayerTimes(daily);
}

// Fetch and cache timetable data for the next 7 days
export async function prefetchNext7DaysTimetable(
  masjidId: number | string,
): Promise<PrayerTimes[]> {
  const timetables: PrayerTimes[] = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    const dateStr = date.toISOString().split("T")[0];
    const [year, month, day] = dateStr.split("-");

    try {
      const timetable = await fetchPrayerTimes(dateStr, masjidId);
      timetables.push(timetable);
    } catch (error) {
      console.error(`Failed to fetch prayer times for ${dateStr}:`, error);
    }
  }

  return timetables;
}

/**
 * Fetch prayer times for the next N days
 * @param masjidId - Masjid identifier
 * @param days - Number of days to fetch (default: 7)
 * @returns Array of PrayerTimes for consecutive days
 */
export async function fetchNext7DaysPrayerTimes(
  masjidId: number | string,
  days: number = 7,
): Promise<PrayerTimes[]> {
  const cacheKey = `next${days}days:${masjidId}`;

  // Check if there's already a pending request for this data
  if (pendingRequests.has(cacheKey)) {
    console.log(
      `Reusing in-flight request for next ${days} days of prayer times`,
    );
    return pendingRequests.get(cacheKey) as Promise<PrayerTimes[]>;
  }

  const fetchPromise = (async () => {
    try {
      const result: PrayerTimes[] = [];
      const today = new Date();

      for (let i = 0; i <= days; i++) {
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + i);

        const year = targetDate.getFullYear().toString();
        const month = (targetDate.getMonth() + 1).toString().padStart(2, "0");
        const day = targetDate.getDate().toString().padStart(2, "0");
        const dateStr = `${year}-${month}-${day}`;

        try {
          const prayerTime = await fetchPrayerTimes(dateStr, masjidId);
          result.push(prayerTime);
        } catch (error) {
          console.error(`Failed to fetch prayer times for ${dateStr}:`, error);
          // Continue with other dates even if one fails
        }
      }

      console.log(
        `📅 Fetched ${result.length} days of prayer times for masjid ${masjidId}`,
      );
      return result;
    } finally {
      // Remove from pending requests after a delay
      setTimeout(() => {
        pendingRequests.delete(cacheKey);
      }, DEDUPLICATION_TTL);
    }
  })();

  // Store the promise for potential reuse
  pendingRequests.set(cacheKey, fetchPromise);
  return fetchPromise;
}

function normalizeHijriDate(h: any): HijriDateComponents {
  if (!h)
    return {
      day: 0,
      month: 0,
      year: 0,
      formattedDate: "--",
    };

  // Handle the new nested structure with shortFormattedDate
  if (typeof h === "object" && h.shortFormattedDate) {
    return {
      day: h.day ?? h.Day ?? 0,
      month: h.month ?? h.Month ?? 0,
      year: h.year ?? h.Year ?? 0,
      monthName: h.monthName ?? h.MonthName,
      formattedDate: h.shortFormattedDate, // Use shortFormattedDate as the main display
      shortFormattedDate: h.shortFormattedDate,
    };
  }

  // Handle legacy format
  return {
    day: h.day ?? h.Day ?? 0,
    month: h.month ?? h.Month ?? 0,
    year: h.year ?? h.Year ?? 0,
    monthName: h.monthName ?? h.MonthName,
    formattedDate:
      h.formattedDate ??
      h.FormattedDate ??
      h.shortFormattedDate ??
      h.ShortFormattedDate ??
      "--",
    shortFormattedDate: h.shortFormattedDate ?? h.ShortFormattedDate,
  };
}

export function transformToPrayerTimes(d: any): PrayerTimes {
  const hijriComponents = normalizeHijriDate(d.HijriDate || d.hijriDate);

  return {
    Date: d.Date || d.date || "--",
    Month: d.Month || d.month || "--",
    Year: d.Year || d.year || "--",
    BegFajr: d.BegFajr || d.begFajr || "--:--",
    JamFajr: d.JamFajr || d.jamFajr || "--:--",
    Sunrise: d.Sunrise || d.sunrise || "--:--",
    BegDhuhr: d.BegDhuhr || d.begDhuhr || "--:--",
    JamDhuhr: d.JamDhuhr || d.jamDhuhr || "--:--",
    BegAsar: d.BegAsar || d.begAsar || "--:--",
    BegAsar2: d.BegAsar2 || d.begAsar2 || "--:--",
    JamAsar: d.JamAsar || d.jamAsar || "--:--",
    Sunset: d.Sunset || d.sunset || "--:--",
    BegMaghrib: d.BegMaghrib || d.begMaghrib || "--:--",
    JamMaghrib: d.JamMaghrib || d.jamMaghrib || "--:--",
    BegEsha: d.BegEsha || d.begEsha || "--:--",
    JamEsha: d.JamEsha || d.jamEsha || "--:--",
    SehriEnd: d.SehriEnd || d.sehriEnd || "--:--",
    Jummah1: d.Jummah1 || d.jummah1 || "--:--",
    Jummah2: d.Jummah2 || d.jummah2 || "--:--",
    Jummah3: d.Jummah3 || d.jummah3 || "--:--",
    Jummah4: d.Jummah4 || d.jummah4 || "--:--",
    Jummah5: d.Jummah5 || d.jummah5 || "--:--",
    JamFajr2: d.JamFajr2 || d.jamFajr2 || "--:--",
    JamDhuhr2: d.JamDhuhr2 || d.jamDhuhr2 || "--:--",
    JamAsar2: d.JamAsar2 || d.jamAsar2 || "--:--",
    JamMaghrib2: d.JamMaghrib2 || d.jamMaghrib2 || "--:--",
    JamEsha2: d.JamEsha2 || d.jamEsha2 || "--:--",
    HijriDate: hijriComponents.formattedDate, // Keep string for backward compatibility
    HijriDateComponents: hijriComponents, // Full components for Islamic event scheduling
    GregorianDate: d.Date || d.date || "--",
    Noon: d.Noon || d.noon || "--:--",
  };
}

// Pre-cache current and next month
export async function precacheTimetableData(masjidId: string | number) {
  const now = new Date();
  const year = now.getFullYear().toString();
  const month = (now.getMonth() + 1).toString();
  let nextMonth = (now.getMonth() + 2).toString();
  let nextYear = year;
  if (nextMonth === "13") {
    nextMonth = "1";
    nextYear = (now.getFullYear() + 1).toString();
  }
  await fetchTimetableData(year, month, masjidId);
  await fetchTimetableData(nextYear, nextMonth, masjidId);
}
